import { Box, Button, Divider, HStack, Spinner, Text } from "@chakra-ui/react";
import {
  BraintreePayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import axios from "axios";
import { useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaCreditCard, FaShopify } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { BRAINTREE_COMPLETE_CHECKOUT_ENDPOINT } from "../../_actions/endpoints";
import { getSubscription } from "../../_actions/subscriptionActions";
import { analyticsTrackEvent } from "../../_utils/common";
import { BILLING_AND_PAYMENT } from "../../_utils/routes";
import { showToast } from "../../_utils/showToast";
import { useHistory } from "react-router-dom";
import { currencyFormatter } from "../../_utils/currencyFormatter";
import { PaymentSources } from "../../_actions/types";
import {
  BILLING_PLAN_INTERVAL_MONTHLY,
  BILLING_PLAN_INTERVAL_YEARLY,
} from "../../_components/details/constants";

const style = {
  label: "checkout",
  layout: "vertical",
  size: "responsive",
  height: 40,
};

const ButtonWrapper = ({
  setLoading,
  selectedPlan,
  onClose,
  dispatch,
  history,
  setIsPaypalLoading,
  totalAmount,
}) => {
  const { t } = useTranslation(["billing"]);
  const [{ isPending }] = usePayPalScriptReducer();
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setIsPaypalLoading(isPending);
    }
    return () => {
      isMounted = false;
    };
  }, [isPending, setIsPaypalLoading]);
  return (
    <BraintreePayPalButtons
      style={style}
      disabled={false}
      fundingSource="paypal" // Available values are: ["paypal", "card", "credit", "paylater", "venmo"]
      forceReRender={[style]}
      createBillingAgreement={function (data, actions) {
        return actions.braintree.createPayment({
          // Required
          flow: "vault",
        });
      }}
      onCancel={() => {
        setLoading(false);
      }}
      onError={(err) => {
        showToast({
          status: "error",
          title: t("braintree_modal.error.title"),
          description: t("braintree_modal.error.description"),
        });
        setLoading(false);
      }}
      onApprove={function (data, actions) {
        setLoading(true);
        return actions.braintree.tokenizePayment(data).then((payload) => {
          // Your code here after capture the order
          const { nonce } = payload;
          axios
            .post(BRAINTREE_COMPLETE_CHECKOUT_ENDPOINT, {
              nonce: nonce,
              plan: selectedPlan.id,
            })
            .then((res) => {
              setLoading(false);
              dispatch(getSubscription());
              analyticsTrackEvent("subscribed", {
                plan: selectedPlan.id,
                unit: selectedPlan.unit_amount,
              });
              showToast({
                title: t("braintree_modal.success.title"),
                description: t("braintree_modal.success.description"),
                status: "success",
              });
              onClose();
              history.push(BILLING_AND_PAYMENT);
            })
            .catch((error) => {
              setLoading(false);
              onClose();
              showToast({
                status: "error",
                description: error.response.data[0],
                title: "Error",
              });
            });
        });
      }}
    />
  );
};

const TldrPaymentSummary = ({
  setIsPaypalLoading,
  selectedPlan,
  cartItems,
  handleStripeCheckout,
  isLoading,
  clientToken,
  onClose,
}) => {
  const { t } = useTranslation(["billing"]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const dispatch = useDispatch();
  const subscription = useSelector((state) => state.subscription);
  let isShopify =
    subscription?.subscribedPlan?.source === PaymentSources.SHOPIFY;

  return (
    <>
      <Box display="flex" justifyContent="center">
        <HStack w="full" alignItems="stretch" align="flex-start">
          <Box flexBasis={0} flexGrow={1}>
            <Text mb={2} fontWeight={500} fontSize="lg" color="white">
              {t("braintree_modal.summary")}
            </Text>
            <Text fontSize="sm" color="white" mb={4}>
              {t("braintree_modal.currency")}
              <Text ml={1} as="span" color={"brand.primary"}>
                {selectedPlan?.currency
                  ? selectedPlan?.currency.toUpperCase()
                  : "USD"}
              </Text>
            </Text>

            <Divider />
            {cartItems?.items?.map((item) => (
              <HStack
                key={item.id}
                justify="space-between"
                align={"flex-start"}
                my={3}
              >
                <Text fontSize="md" color="gray.100">
                  {item.extra?.product_name}{" "}
                  {item.extra?.interval &&
                    `(${
                      item.extra?.interval === BILLING_PLAN_INTERVAL_MONTHLY
                        ? t("choose_plan_and_pay_modal.month")
                        : item.extra?.interval === BILLING_PLAN_INTERVAL_YEARLY
                        ? t("choose_plan_and_pay_modal.year")
                        : null
                    })`}
                </Text>
                <Text fontSize="md" color="gray.100" fontWeight={500}>
                  {item?.quantity && item?.product_type === "seats"
                    ? `${item?.quantity} x ${currencyFormatter(
                        item?.unit_price,
                        subscription?.subscribedPlan?.plan?.currency
                      )}`
                    : `${currencyFormatter(
                        item?.unit_price,
                        subscription?.subscribedPlan?.plan?.currency
                      )}`}
                </Text>
              </HStack>
            ))}

            <Divider />
            <HStack justify="space-between" align="flex-start" my={3}>
              <Text color={"brand.white"} fontSize="md" fontWeight={500}>
                {t("braintree_modal.total")}
              </Text>
              <Text color={"brand.white"} fontSize="md" fontWeight={500}>
                {currencyFormatter(
                  cartItems?.total,
                  subscription?.subscribedPlan?.plan?.currency
                )}
              </Text>
            </HStack>
            <Divider />
          </Box>
        </HStack>
      </Box>
      {loading && (
        <Box textAlign="center">
          <Spinner size={"xl"} color="brand.primary" />
        </Box>
      )}
      <Box w="full">
        <Text my={3} fontSize="sm">
          {t("braintree_modal.pay_via")}
        </Text>
        <Button
          isLoading={isLoading}
          textAlign="left"
          leftIcon={isShopify ? <FaShopify /> : <FaCreditCard />}
          borderRadius={4}
          w="full"
          onClick={() => handleStripeCheckout(selectedPlan)}
          fontWeight="400"
          mr={4}
        >
          {isShopify ? t("braintree_modal.shopify") : t("braintree_modal.card")}
        </Button>
        {!isShopify && (
          <>
            {clientToken ? (
              <Box w="full" mt={4}>
                <PayPalScriptProvider
                  options={{
                    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                    components: "buttons",
                    // "data-user-id-token": "your-tokenization-key-here",
                    "data-client-token": clientToken,
                    intent: "tokenize",
                    vault: true,
                    commit: true,
                  }}
                >
                  <ButtonWrapper
                    setIsPaypalLoading={setIsPaypalLoading}
                    setLoading={setLoading}
                    selectedPlan={selectedPlan}
                    onClose={onClose}
                    dispatch={dispatch}
                    history={history}
                    totalAmount={cartItems.total}
                  />
                </PayPalScriptProvider>
              </Box>
            ) : (
              <Button w="full" mt={3} borderRadius={4}>
                <Spinner color="brand.primary" />
              </Button>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default TldrPaymentSummary;
